import type { AvailableSecret } from '@/hooks/secrets/useAvailableSecrets';

/**
 * Builds the picker's section list. Order is fixed:
 *
 * 1. "Your Secrets" — user-tier rows (`category: 'user'`) the user added
 *    directly via `/me/secrets`. Only one group.
 * 2. "Team: <name>" — team-tier rows (`category: 'team'`), one group per
 *    distinct `provider` (team name). Sorted alphabetically by team name.
 * 3. "Organization" — `category: 'custom'`, org-tier secrets added directly
 *    via Admin → Secrets. Single trailing group.
 *
 * Those three are the whole list: `/secrets/available` returns only what
 * the organization created to hand to agents. Integration credentials
 * (model-provider keys, `RUNLOOP_API_KEY`, plugin and service-tier rows)
 * are never listed and are refused at launch, so there is no
 * provider-grouped section here. Empty groups are omitted entirely so a
 * picker fed with only org-tier rows still renders a sensible layout.
 */
export function groupSecretsByCategory(
  secrets: AvailableSecret[],
): Array<{ label: string; secrets: AvailableSecret[] }> {
  const user = secrets.filter((s) => s.category === 'user');
  const teamRows = secrets.filter((s) => s.category === 'team');
  const custom = secrets.filter((s) => s.category === 'custom');

  const groups: Array<{ label: string; secrets: AvailableSecret[] }> = [];

  if (user.length) groups.push({ label: 'Your Secrets', secrets: user });

  const byTeam = new Map<string, AvailableSecret[]>();
  for (const secret of teamRows) {
    const key = secret.provider ?? 'Unknown team';
    const list = byTeam.get(key);
    if (list) list.push(secret);
    else byTeam.set(key, [secret]);
  }
  const teamKeys = Array.from(byTeam.keys()).sort((a, b) => a.localeCompare(b));
  for (const key of teamKeys) {
    groups.push({ label: `Team: ${key}`, secrets: byTeam.get(key) ?? [] });
  }

  if (custom.length) groups.push({ label: 'Organization', secrets: custom });
  return groups;
}
